import React from "react";
import { Button, Form, Input, Space, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import { inviteSupplier, previewEmailTemplate, validateComplianceCase } from "../../api";
import type { ComplianceCaseDetail, SupplierInvite } from "../types";

type Props = {
  caseData: ComplianceCaseDetail;
  onUpdated: () => void;
  validating: boolean;
  setValidating: (v: boolean) => void;
};

async function copyInviteUrl(url: string, copiedLabel: string, failedLabel: string) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
    } else {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    message.success(copiedLabel);
  } catch {
    message.error(failedLabel);
  }
}

function InviteUrlActions({ url }: { url: string }) {
  const { t } = useTranslation();
  return (
    <Space wrap align="start" style={{ width: "100%" }}>
      <Typography.Paragraph style={{ marginBottom: 0, wordBreak: "break-all", flex: 1 }}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      </Typography.Paragraph>
      <Button
        size="small"
        onClick={() =>
          void copyInviteUrl(
            url,
            t("compliance.resolution.inviteLinkCopied"),
            t("compliance.resolution.copyInviteFailed")
          )
        }
      >
        {t("compliance.resolution.copyInviteLink")}
      </Button>
    </Space>
  );
}

export function ResolutionPanel({ caseData, onUpdated, validating, setValidating }: Props) {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [lastInvite, setLastInvite] = React.useState<SupplierInvite | null>(null);
  const [emailPreview, setEmailPreview] = React.useState<{ subject: string; body: string } | null>(
    null
  );

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <p>{t("compliance.resolution.intro")}</p>
      <Form
        form={form}
        layout="inline"
        onFinish={async (values: { email: string }) => {
          try {
            const updated = await inviteSupplier(caseData.id, {
              email: values.email,
              locale: i18n.language,
            });
            const match =
              updated.supplier_invites
                .filter((inv) => inv.email === values.email)
                .sort(
                  (a, b) => new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime()
                )[0] ?? null;
            setLastInvite(match);
            try {
              const preview = await previewEmailTemplate(
                "compliance.supplier.invited",
                i18n.language
              );
              setEmailPreview({ subject: preview.subject, body: preview.body });
            } catch {
              setEmailPreview(null);
            }
            message.success(t("compliance.resolution.inviteSent"));
            form.resetFields();
            onUpdated();
          } catch {
            message.error(t("compliance.resolution.inviteFailed"));
          }
        }}
      >
        <Form.Item name="email" rules={[{ required: true, type: "email" }]}>
          <Input placeholder={t("compliance.resolution.emailPlaceholder")} style={{ width: 280 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            {t("compliance.resolution.inviteSupplier")}
          </Button>
        </Form.Item>
      </Form>
      {lastInvite?.invite_url && (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Typography.Text strong>{t("compliance.resolution.inviteLinkLabel")}</Typography.Text>
          <InviteUrlActions url={lastInvite.invite_url} />
        </Space>
      )}
      {emailPreview && (
        <Space direction="vertical" size={4} style={{ width: "100%" }} data-testid="invite-email-preview">
          <Typography.Text strong>{t("compliance.resolution.emailPreview")}</Typography.Text>
          <Typography.Text type="secondary">{emailPreview.subject}</Typography.Text>
          <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
            {emailPreview.body}
          </Typography.Paragraph>
        </Space>
      )}
      <Button
        type="default"
        loading={validating}
        onClick={async () => {
          setValidating(true);
          try {
            await validateComplianceCase(caseData.id);
            message.success(t("compliance.resolution.revalidationComplete"));
            onUpdated();
          } catch {
            message.error(t("compliance.caseDetail.validationFailed"));
          } finally {
            setValidating(false);
          }
        }}
      >
        {t("compliance.resolution.revalidate")}
      </Button>
      {caseData.supplier_invites.length > 0 && (
        <>
          <Typography.Text type="secondary">{t("compliance.resolution.existingInvites")}</Typography.Text>
          <ul>
            {caseData.supplier_invites.map((inv) => (
              <li key={inv.id}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <span>
                    {inv.email} — {t("common.expires")}{" "}
                    {new Date(inv.expires_at).toLocaleDateString()}
                    {inv.accepted_at ? ` ${t("common.accepted")}` : ` ${t("common.pending")}`}
                  </span>
                  {inv.invite_url ? <InviteUrlActions url={inv.invite_url} /> : null}
                </Space>
              </li>
            ))}
          </ul>
        </>
      )}
    </Space>
  );
}
